<script setup lang="ts">
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeInformation from "@dsk/icons/LeInformation.vue";

import type { DictsysInflectionSchemeTable } from "@/types";

import LeInflectionSchemeHighlight from "./LeInflectionSchemeHighlight.vue";

const props = defineProps<{
  tables: DictsysInflectionSchemeTable[];
  language: string;
}>();

const emit = defineEmits<{
  toggleExtraTable: [table: DictsysInflectionSchemeTable];
}>();

function getMaxColumns(table: DictsysInflectionSchemeTable): number {
  return Math.max(...table.cells.map((c) => c.length));
}

function getFormattedText(text: string[] | undefined) {
  if (text === undefined) {
    return "";
  }
  return text.join("");
}

const onToggleExtraTable = (table: DictsysInflectionSchemeTable) => {
  emit("toggleExtraTable", table);
};
</script>

<template>
  <table class="le-inflection-scheme-table">
    <template v-for="(table, tableIndex) in props.tables" :key="tableIndex">
      <template v-if="table.cells.length > 0">
        <tbody class="le-inflection-scheme-table__table-body">
          <tr v-if="table.header || table.comment">
            <th :colspan="getMaxColumns(table)" class="le-inflection-scheme-table__header">
              <span class="le-inflection-scheme-table__header-content">
                <h6 class="le-inflection-scheme-table__header-text">
                  {{ table.header }}

                  <template v-if="table.comment">
                    {{ table.comment }}
                  </template>
                </h6>

                <span v-if="table.extraTable">
                  <LeTooltip :text="table.extraTable.text">
                    <LeIconButton
                      size="s"
                      :aria-label="table.extraTable.label"
                      @click="onToggleExtraTable(table.extraTable)"
                    >
                      <LeInformation role="presentation" />
                    </LeIconButton>
                  </LeTooltip>
                </span>
              </span>
            </th>
          </tr>
          <tr v-for="(row, rowIndex) in table.cells" :key="rowIndex">
            <template v-for="(cell, cellIndex) in row" :key="cellIndex">
              <td
                :class="{
                  'le-inflection-scheme-table__separator': table.separatorIndex === rowIndex,
                  'le-inflection-scheme-table__sub-header': cell.type === 'sub-header',
                  'le-inflection-scheme-table__body': cell.type === 'body'
                }"
              >
                <div class="le-inflection-scheme-table__cell">
                  <template v-for="(element, elementIndex) in cell.elements" :key="elementIndex">
                    <span
                      :class="{
                        'le-inflection-scheme-table__text--grid': cell.type === 'sub-header' && rowIndex === 0
                      }"
                    >
                      <span
                        v-if="cell.type === 'sub-header'"
                        class="le-inflection-scheme-table__text--bold le-inflection-scheme-table__text--space-right"
                        :class="{ 'le-inflection-scheme-table__text--with-icon-button': row.some((c) => c.extraTable) }"
                      >
                        {{ getFormattedText(element.texts) }}

                        <span v-if="cell.extraTable">
                          <LeTooltip :text="cell.extraTable.text">
                            <LeIconButton
                              size="s"
                              :aria-label="cell.extraTable.label"
                              @click="onToggleExtraTable(cell.extraTable)"
                            >
                              <LeInformation role="presentation" />
                            </LeIconButton>
                          </LeTooltip>
                        </span>
                      </span>
                      <span v-if="element.comment" class="le-inflection-scheme-table__text--italic">
                        {{ element.comment }}
                      </span>
                    </span>

                    <span
                      v-if="element.auxiliaryVerbs?.length"
                      class="le-inflection-scheme-table__text le-inflection-scheme-table__text--light le-inflection-scheme-table__text--space-right"
                    >
                      {{ element.auxiliaryVerbs.join("/") }}
                    </span>

                    <span
                      v-if="cell.type === 'body' && element.texts"
                      :class="{
                        'le-inflection-scheme-table__text--space-right': element.texts.length === 1,
                        'le-inflection-scheme-table__text--grid': element.texts.length > 1
                      }"
                    >
                      <template v-for="(text, index) in element.texts" :key="text + index">
                        <LeInflectionSchemeHighlight v-if="text" :language="props.language" :text="text" />
                      </template>
                    </span>

                    <LeTooltip
                      v-if="element.tooltip"
                      :text="element.tooltip"
                      :margin-bottom="10"
                      class="le-inflection-scheme-table__text le-inflection-scheme-table__text--space-right le-inflection-scheme-table__info-wrapper"
                    >
                      <LeInformation class="le-inflection-scheme-table__info" />
                    </LeTooltip>

                    <span
                      v-if="elementIndex < cell.elements.length - 1"
                      class="le-inflection-scheme-table__text le-inflection-scheme-table__text--space-right"
                    >
                      /
                    </span>
                  </template>
                </div>
              </td>
            </template>
          </tr>
        </tbody>

        <tbody v-if="tableIndex !== props.tables.length - 1" aria-hidden="true">
          <tr class="le-inflection-scheme-table__footer">
            <td :colspan="getMaxColumns(table)"></td>
          </tr>
        </tbody>
      </template>
    </template>
  </table>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-inflection-scheme-table {
  width: 100%;
  word-break: keep-all;
  hyphens: manual;

  &__footer {
    height: 10px;

    #media-breakpoint-up(lg, {
      height: 30px;
    });
  }

  &__header,
  &__sub-header,
  &__body {
    border: 1px solid @colors[@secondary][600];
    color: @colors[@neutrals][500];
    vertical-align: top;
    .dictsys-inflection-scheme-text();
  }

  &__sub-header,
  &__body {
    height: 34px;
    padding: 8px;
  }

  &__header {
    height: 36px;
    padding: 8px 16px;
    background-color: @colors[@secondary][500];

    &-content {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }

  &__sub-header {
    background-color: @colors[@secondary][300];
  }

  &__body {
    background-color: @colors[@neutrals][100];
  }

  &__header-text {
    margin-top: 0;
    margin-bottom: 0;
    .dictsys-inflection-scheme-text();
    .dictsys-inflection-scheme-text--bold();
    .dictsys-inflection-scheme-text--large();
  }

  &__separator {
    border-collapse: collapse;
    border-bottom: 3px solid @colors[@secondary][600];
  }

  &__cell {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  &__text {
    &--space-right {
      margin-right: 5px;
    }

    &--grid {
      display: grid;
      width: 100%;
      height: 100%;
    }

    &--bold {
      .dictsys-inflection-scheme-text--bold();
    }

    &--italic {
      .dictsys-inflection-scheme-text--italic();
    }

    &--light {
      color: @colors[@neutrals][200];
    }

    &--with-icon-button {
      #media-breakpoint-up(md, {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        min-height: 24px;
      });
    }
  }

  &__info-wrapper {
    line-height: 0;
  }

  &__info {
    color: @colors[@neutrals][400];
  }

  &__schema-highlight {
    background-color: @colors[@accent][300];
  }
}
</style>
